import { Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import SvgDollar from 'components/svg/SvgDollar';
import MainSettingsAside from './MainSettingsAside';
import { GROUPS, isSettingsGroup, type SettingsGroup } from './model/groups';
import useMainSettings from './model/useMainSettings';
import CurrencyTab from './tabs/CurrencyTab';
import ObjectsTab from './tabs/ObjectsTab';
import TasksTab from './tabs/TasksTab';
import WarehouseTab from './tabs/WarehouseTab';

const MainSettings = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const { params, isDirty, isSaving, setParam, handleSave } = useMainSettings();
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('activeTab');
    const group: SettingsGroup = isSettingsGroup(tabFromUrl) ? tabFromUrl : 'currency';
    const setGroup = (key: SettingsGroup) => {
        setSearchParams(key === 'currency' ? {} : { activeTab: key }, { replace: true });
    };
    const ActiveIcon = GROUPS.find((item) => item.key === group)?.Icon || SvgDollar;

    return (
        <Row>
            <Col sm={12}>
                <div className="main-settings-container">
                    <div className="w-100 d-flex pt-1 justify-content-between align-items-start mb-3 mb-md-2">
                        <div className="pb-1">
                            <h4 className="p-0 m-0">{t('mainParams.title')}</h4>
                        </div>
                    </div>

                    <Row className="g-3 main-settings-layout">
                        <Col xs={12} md={4} lg={3}>
                            <MainSettingsAside group={group} onSelect={setGroup} />
                        </Col>

                        <Col xs={12} md={8} lg={9}>
                            <Card className="main-settings-panel border-0 shadow-sm h-100">
                                <Card.Header className="bg-transparent border-bottom py-3 px-3 px-md-4">
                                    <div className="d-flex align-items-start gap-2 min-w-0">
                                        <span className="main-settings-panel-icon text-primary">
                                            <ActiveIcon width={20} height={20} />
                                        </span>
                                        <div className="min-w-0">
                                            <h5 className="mb-1">
                                                {t(`mainParams.groups.${group}.title`)}
                                            </h5>
                                            <small className="text-muted">
                                                {t(`mainParams.groups.${group}.subtitle`)}
                                            </small>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-3 p-md-4">
                                    {group === 'currency' && (
                                        <CurrencyTab
                                            params={params}
                                            isSaving={isSaving}
                                            setParam={setParam}
                                        />
                                    )}
                                    {group === 'warehouse' && (
                                        <WarehouseTab
                                            params={params}
                                            isSaving={isSaving}
                                            setParam={setParam}
                                        />
                                    )}
                                    {group === 'objects' && (
                                        <ObjectsTab
                                            params={params}
                                            isSaving={isSaving}
                                            setParam={setParam}
                                        />
                                    )}
                                    {group === 'tasks' && (
                                        <TasksTab
                                            params={params}
                                            isSaving={isSaving}
                                            setParam={setParam}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                            <div className="d-flex justify-content-end mt-3">
                                <Button
                                    size="sm"
                                    variant="success"
                                    disabled={!isDirty || isSaving}
                                    onClick={handleSave}
                                >
                                    {isSaving ? (
                                        <>
                                            <Spinner
                                                animation="border"
                                                size="sm"
                                                className="me-2"
                                            />
                                            {t('mainParams.saving')}
                                        </>
                                    ) : (
                                        t('mainParams.save')
                                    )}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Col>
        </Row>
    );
};

export default MainSettings;
